import localforage from "localforage";
import { supabase } from "@/integrations/supabase/client";
import { OfflineConversation } from "@/types/chat";
import { LOCALFORAGE_CONFIG, DB_TABLES } from "@/constants";

// Configure localforage for offline storage
const conversationStore = localforage.createInstance({
  name: LOCALFORAGE_CONFIG.DB_NAME,
  storeName: LOCALFORAGE_CONFIG.STORES.CONVERSATIONS,
});

const documentStore = localforage.createInstance({
  name: LOCALFORAGE_CONFIG.DB_NAME,
  storeName: LOCALFORAGE_CONFIG.STORES.DOCUMENTS,
});

const metadataStore = localforage.createInstance({
  name: LOCALFORAGE_CONFIG.DB_NAME,
  storeName: LOCALFORAGE_CONFIG.STORES.METADATA,
});

/**
 * Save conversation to offline storage
 */
export const saveConversationOffline = async (conversation: OfflineConversation) => {
  try {
    await conversationStore.setItem(conversation.id, conversation);
  } catch (error) {
    console.error("Error saving conversation offline:", error);
  }
};

/**
 * Get all offline conversations
 */
export const getOfflineConversations = async (): Promise<OfflineConversation[]> => {
  try {
    const conversations: OfflineConversation[] = [];
    await conversationStore.iterate<OfflineConversation, void>((value) => {
      conversations.push(value);
    });
    return conversations.sort((a, b) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
  } catch (error) {
    console.error("Error getting offline conversations:", error);
    return [];
  }
};

/**
 * Get a specific offline conversation
 */
export const getOfflineConversation = async (id: string): Promise<OfflineConversation | null> => {
  try {
    return await conversationStore.getItem<OfflineConversation>(id);
  } catch (error) {
    console.error("Error getting offline conversation:", error);
    return null;
  }
};

/**
 * Sync conversations from server to offline storage
 */
export const syncConversationsToOffline = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get conversations
    const { data: conversations } = await supabase
      .from(DB_TABLES.CONVERSATIONS)
      .select("id, title, user_id, created_at, updated_at, is_starred")
      .order("updated_at", { ascending: false })
      .limit(50); // Only sync recent conversations

    if (!conversations) return;

      // For each conversation, get messages and save offline
      for (const conv of conversations) {
        const { data: messages } = await supabase
          .from(DB_TABLES.CHAT_MESSAGES)
          .select("role, content, created_at")
          .eq("conversation_id", conv.id)
          .order("created_at", { ascending: true });

        await saveConversationOffline({
          ...conv,
          messages: (messages || []).map(msg => ({
            role: msg.role as "user" | "assistant",
            content: msg.content,
            created_at: msg.created_at,
          })),
        });
      }
  } catch (error) {
    console.error("Error syncing conversations:", error);
  }
};

/**
 * Check if online
 */
export const isOnline = () => navigator.onLine;

/**
 * Setup online/offline listeners
 */
export const setupOfflineListeners = (
  onOnline: () => void,
  onOffline: () => void
) => {
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);

  return () => {
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
  };
};

/**
 * Save documents list to offline storage
 */
export const saveDocumentsOffline = async (documents: any[]) => {
  try {
    await documentStore.setItem("document_list", documents);
    await metadataStore.setItem("document_list_timestamp", Date.now());
  } catch (error) {
    console.error("Error saving documents offline:", error);
  }
};

/**
 * Get offline documents list
 */
export const getOfflineDocuments = async (): Promise<any[]> => {
  try {
    const documents = await documentStore.getItem<any[]>("document_list");
    return documents || [];
  } catch (error) {
    console.error("Error getting offline documents:", error);
    return [];
  }
};

/**
 * Check if offline data is stale (older than 24 hours)
 */
export const isOfflineDataStale = async (key: string): Promise<boolean> => {
  try {
    const timestamp = await metadataStore.getItem<number>(`${key}_timestamp`);
    if (!timestamp) return true;
    const hoursSinceUpdate = (Date.now() - timestamp) / (1000 * 60 * 60);
    return hoursSinceUpdate > 24;
  } catch (error) {
    return true;
  }
};

/**
 * Sync documents from server to offline storage
 */
export const syncDocumentsToOffline = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: documents } = await supabase
      .from(DB_TABLES.PERMANENT_DOCUMENTS)
      .select("id, title, filename, file_type, status, uploaded_at, category")
      .eq("status", "ready")
      .order("uploaded_at", { ascending: false })
      .limit(100);

    if (documents) {
      await saveDocumentsOffline(documents);
    }
  } catch (error) {
    console.error("Error syncing documents:", error);
  }
};
